export type MetricParams = Record<string, string | number | boolean>;
export type GaugeParams = Record<string, number>;
export type MetricTags = Record<string, string>;

// Note if you're adding a new event name:
// 1) It should be in the AEL client config: https://git.corp.stripe.com/stripe-internal/zoolander/blob/a6bcdd07e65dbd9e79340ef6a6715a5ec55c475d/src/resources/com/stripe/dscore/analyticseventlogger/server/rpcserver/client_config.yaml#L4090
// 2) In order to be emitted from the dashboard when using the sails-observability library it should be added to manage/lib/platform/metrics/client_metrics.rb
const SUCCESS_EVENTS = {
  SEND_MESSAGE: 'comms.success.send_message',
  RPC_REQUEST: 'comms.success.rpc_request',
  RPC_REPLY: 'comms.success.rpc_reply',
  HANDSHAKE_COMPLETE: 'comms.success.handshake_complete',
  TARGET_VERIFICATION_COMPLETE: 'comms.success.target_verification_complete',
  ON_MESSAGE_RECEIVED: 'comms.success.on_message_received',
  ONCE_MESSAGE_RECEIVED: 'comms.success.once_message_received',
  BROADCAST: 'comms.success.broadcast',
} as const;

const ERROR_EVENTS = {
  TIMEOUT_ERROR: 'comms.error.timeout',
  VALIDATION_ERROR: 'comms.error.validation',
  INPUT_VALIDATION_FAILED: 'comms.error.input_validation_failed',
  OUTPUT_VALIDATION_FAILED: 'comms.error.output_validation_failed',
  TARGET_VERIFICATION_FAILED: 'comms.error.target_verification_failed',
  ORIGIN_VERIFICATION_FAILED: 'comms.error.origin_verification_failed',
  EVENT_SOURCE_VERIFICATION_FAILED:
    'comms.error.event_source_verification_failed',
  COMM_ID_VERIFICATION_FAILED:
    'comms.error.communication_id_verification_failed',
  RPC_RESPONSE_ERROR: 'comms.error.rpc_response',
  HANDSHAKE_ERROR: 'comms.error.handshake',
  SEND_MESSAGE_ERROR: 'comms.error.send_message',
  UNKNOWN_ERROR: 'comms.error.unknown',
} as const;

export const INCREMENT_EVENTS = {
  ...SUCCESS_EVENTS,
  ...ERROR_EVENTS,
} as const;
export type IncrementEvent =
  (typeof INCREMENT_EVENTS)[keyof typeof INCREMENT_EVENTS];

export const GAUGE_EVENTS = {
  HANDSHAKE_DURATION_MS: 'comms.latency.handshake_duration_ms',
  TARGET_VERIFICATION_DURATION_MS:
    'comms.latency.target_verification_duration_ms',
  INPUT_VALIDATION_DURATION_MS: 'comms.latency.input_validation_duration_ms',
  OUTPUT_VALIDATION_DURATION_MS: 'comms.latency.output_validation_duration_ms',
  RPC_CALL_DURATION_MS: 'comms.latency.rpc_call_duration_ms',
} as const;
export type GaugeEvent = (typeof GAUGE_EVENTS)[keyof typeof GAUGE_EVENTS];

export const SET_EVENTS = {} as const;
export type SetEvent = (typeof SET_EVENTS)[keyof typeof SET_EVENTS];

/**
 * Metrics interface defines methods for recording different types of metrics.
 */
export interface Metrics {
  increment: (name: IncrementEvent, tags?: MetricTags) => void;
  gauge: (name: GaugeEvent, duration: number, tags?: MetricTags) => void;
  set: (name: SetEvent, params: MetricParams, tags?: MetricTags) => void;
}
